/**
 * Os textos montados da conferência das etapas (E147.F3): o que a coluna "Conferência da saída" diz
 * de cada etapa, o fundamento de cada critério e o nome de cada consulta. A chave do servidor não
 * vai para a tela: o que não tem texto aqui não aparece.
 */
import { ptBR } from '@/i18n/pt-BR';
import {
  criterioQueTravou,
  type CondicaoDeSaida,
  type CriterioConferidoData,
  type EtapaDoCasoData,
  type FundamentoDoCriterio,
  type ProvedorExterno,
} from '@/lib/conferencia_das_etapas';

import { preencher } from '../FichaDaLeitura/textos';

export { preencher };

export const t = ptBR.conferenciaDasEtapas;

const CONSULTAS = ptBR.historicoDoCaso.detalhe.consultaPorProvedor;

function noTexto<K extends string>(textos: Readonly<Record<K, string>>, chave: string): string {
  return Object.hasOwn(textos, chave) ? textos[chave as K] : '';
}

export function nomeDaConsulta(provedor: ProvedorExterno): string {
  return CONSULTAS[provedor];
}

export function textoDaCondicao(condicao: string | null): string {
  return condicao === null ? '' : noTexto<CondicaoDeSaida>(t.condicao, condicao);
}

/** O fundamento do critério, com as contagens que a consulta respondida leva. */
export function textoDoFundamento(c: CriterioConferidoData): string {
  const partes = [noTexto<FundamentoDoCriterio>(t.fundamento, c.fundamento)];
  const { limitadas, sem_configuracao: semConfiguracao } = c.parametros;
  if (typeof limitadas === 'number' && limitadas > 0) {
    partes.push(preencher(t.limitadas, { n: limitadas }));
  }
  if (typeof semConfiguracao === 'number' && semConfiguracao > 0) {
    partes.push(preencher(t.semConfiguracao, { n: semConfiguracao }));
  }
  return partes.filter(Boolean).join(' ');
}

/** A coluna "Conferência da saída": aguardando com o motivo, aprovada com a contagem, ou o critério
 * que travou e a condição de saída. */
export function textoDaSaida(etapa: EtapaDoCasoData): string {
  if (etapa.agora === null) {
    const aguardando = etapa.aguardando;
    if (aguardando === null) return t.aguardando.nao_executada;
    const anterior = aguardando.depende_de === null ? '' : t.etapa[aguardando.depende_de];
    return preencher(t.aguardando[aguardando.motivo], { etapa: anterior });
  }
  const agora = etapa.agora;
  if (agora.liberacao === 'aprovado') return preencher(t.aprovada, { n: agora.criterios.length });
  const travou = criterioQueTravou(agora);
  const fundamento = travou === null ? '' : textoDoFundamento(travou);
  return [fundamento, textoDaCondicao(agora.condicao_de_saida)].filter(Boolean).join(' ');
}

const MINUTO = 60;
const HORA = 3600;

export function duracao(segundos: number): string {
  if (segundos < MINUTO) return preencher(t.duracao.segundos, { n: segundos });
  if (segundos < HORA) return preencher(t.duracao.minutos, { n: Math.round(segundos / MINUTO) });
  return preencher(t.duracao.horas, { n: Math.round(segundos / HORA) });
}
